import { Textbox } from 'fabric';

const DIM_TOLERANCE = 15;

export class BadgeTextbox extends Textbox {

    calcTextHeight() {
        return this.height;
    }

    _calcTextHeight() {
        let lineHeight,
            height = 0;
        for (let i = 0, len = this._textLines.length; i < len; i++) {
            lineHeight = this.getHeightOfLine(i);
            height += i === len - 1 ? lineHeight / this.lineHeight : lineHeight;
        }
        return height;
    }

    recalculateFontSize() {
        if (!this.useDynamicFontSize) {
            return;
        }

        const originalFontSize = this.fontSize;
        let low = this.minFontSize || 1;
        let high = this.maxFontSize || 100;
        let optimalFontSize = low;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            if (mid <= 0) {
                low = 1;

                continue;
            }

            this.fontSize = mid;
            // wraps text and recalculates textLine sizes
            this.initDimensions();

            const textW = this.calcTextWidth();
            const textH = this._calcTextHeight();

            if (textW <= this.width && textH <= this.height) {
                // text fits, try larger
                optimalFontSize = mid;
                low = mid + 1;
            } else {
                // text doesn't fit, try smaller
                high = mid - 1;
            }
        }

        this.fontSize = optimalFontSize > 0 ? optimalFontSize : originalFontSize;

        this.initDimensions();
        this.canvas.renderAll();
    }

    /**
     * Overwrites Textbox method and allows only to split words on whitespace.
     * Omits using _wordJoiners param, as the method is called with non overwritable parent _wordJoiners value,
     * which then uses '\n', reverting whitespace split.
     *
     * @param {string} value
     * @returns {string[]} array of words
    */
    wordSplit(value) {
        return value.split(' ');
    }

    /**
     * Overwrites Textbox.initDimensions() method and forces max height of the boundary box based on text size.
     *
     * @override
     */
    initDimensions() {
        super.initDimensions();

        if (this.fontSize !== this.minFontSize) {
            return;
        }

        const lineHeight = this.getHeightOfLine(0);
        if (this.height <= lineHeight) {
            this._set('height', lineHeight);
        }
    }

    /**
     * Override of Text._renderTextCommon() method, with slight customization that prevents rendering lines
     * when next line would cross over the Textbox boundary box.
     *
     * @override
     * @param {CanvasRenderingContext2D} ctx Context to render on
     * @param {String} method Method name ("fillText" or "strokeText")
     */
    _renderTextCommon(
        ctx,
        method,
    ) {
        ctx.save();
        let lineHeights = 0;
        const left = this._getLeftOffset(),
            top = this._getTopOffset();

        for (let i = 0, len = this._textLines.length; i < len; i++) {
            const heightOfLine = this.getHeightOfLine(i),
                maxHeight = heightOfLine / this.lineHeight,
                leftOffset = this._getLineLeftOffset(i);

            const currentLineHeight = lineHeights + heightOfLine;
            if (currentLineHeight - DIM_TOLERANCE > this.height) {
                break;
            }

            const currentLine = [...this._textLines[i]];

            const nextLineHeight = currentLineHeight + this.safeGetHeightOfLine(i + 1);
            if (nextLineHeight > this.height) {
                const currentLineWidth = this.getLineWidth(i);

                // just approximate width needed to fit '...' with current font.
                if (currentLineWidth + DIM_TOLERANCE > this.width) {
                    currentLine.splice(-3);
                }

                currentLine.push('.', '.', '.');
            }

            this._renderTextLine(
                method,
                ctx,
                currentLine,
                left + leftOffset,
                top + lineHeights + maxHeight,
                i,
            );

            lineHeights += heightOfLine;
        }
        ctx.restore();
    }

    /**
     * Overwrites Textbox method while disabling ability of long words to resize boundary box in order to fit them.
     * Always setting the width to the one set by user.
     *
     * @private
     * @param {Number} lineIndex
     * @param {Number} desiredWidth width you want to wrap the line to
     * @param {GraphemeData} graphemeData an object containing all the lines' words width.
     * @param {Number} reservedSpace space to remove from wrapping for custom functionalities
     * @returns {Array} Array of line(s) into which the given text is wrapped to.
    */
    _wrapLine(
        lineIndex,
        desiredWidth,
        graphemeData,
        reservedSpace = 0,
    ) {
        const graphemeLines = super._wrapLine(lineIndex, desiredWidth, graphemeData, reservedSpace);

        // call after _wrapLine is done, as it originally reassigns dynamicMinWidth at the end.
        // NOTE: in future versions this might be controlled somewhere else and brake
        // - per their comment in _wrapLine at version 6.6.1
        this.dynamicMinWidth = desiredWidth;

        return graphemeLines;
    }

    safeGetHeightOfLine(index) {
        if (this._textLines[index]) {
            return this.getHeightOfLine(index);
        }
    }
}
